import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import type { LdFeatureFlags } from '@/config/features';
import { useProfileAgreement } from '@/hooks/queries/use-profile-agreement';
import { useProfileClientCategorisation } from '@/hooks/queries/use-profile-client-categorisation';
import { useProfileClientCategorisationTreatment } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useProfileVerificationInformationSupportingInformation } from '@/hooks/queries/use-profile-verification-information-supporting-information';
import LoadingPage from '@/pages/loading';
import { FUTURES_VERIFICATION_LEVEL_IDENTIFIERS, WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import { AgreementKey } from '@/services/api/profile/agreement';

const FuturesInitialRedirect = () => {
  const { isLoading: isLoadingAgreement, data: servicesAgreement } = useProfileAgreement(AgreementKey.SERVICES_AGREEMENT);
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const { nciPriorityFlowEnabled } = useFlags<LdFeatureFlags>();
  const isFuturesVerificationLevel = nciPriorityFlowEnabled
    ? FUTURES_VERIFICATION_LEVEL_IDENTIFIERS.some((identifier) => verificationInformation?.verification?.level?.includes(identifier))
    : !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const isWorkflowVerificationLevel = !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const { isLoading: isLoadingVerificationInformationSupportingInformation, isComplete: isCompleteVerificationInformationSupportingInformationQuery } =
    useProfileVerificationInformationSupportingInformation(isWorkflowVerificationLevel);
  const { isLoading: isLoadingClientCategorisation, data: clientCategorisation } = useProfileClientCategorisation();
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const { isLoading: isLoadingFuturesAppropriateness, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const { t } = useTranslation();
  const isCompleteVerificationInformationSupportingInformation = isWorkflowVerificationLevel ? !!isCompleteVerificationInformationSupportingInformationQuery : true;
  const isLoading =
    isLoadingAgreement ||
    isLoadingVerificationInformation ||
    isLoadingVerificationInformationSupportingInformation ||
    isLoadingClientCategorisation ||
    isLoadingClientCategorisationTreatment ||
    isLoadingFuturesAppropriateness;

  if (isLoading)
    return (
      <LoadingPage>
        <p>{t('redirectingToNextStep')}</p>
      </LoadingPage>
    );

  if (!servicesAgreement?.date) return <Navigate to="/futures/services-agreement" replace />;

  if (!verificationInformation || verificationInformation.verification.status === 'NOT_VERIFIED' || !isFuturesVerificationLevel)
    return <Navigate to="/futures/verification" replace />;

  if (!isCompleteVerificationInformationSupportingInformation) return <Navigate to="/futures/verification/supporting-information" replace />;

  if (!clientCategorisation) return <Navigate to="/futures/client-categorisation" replace />;

  if (!clientCategorisationTreatment) return <Navigate to="/futures/client-categorisation/treatment" />;

  if (!futuresAppropriateness || !('result' in futuresAppropriateness)) return <Navigate to="/futures/appropriateness-test" replace />;

  return <Navigate to="/futures/appropriateness-test/result" replace />;
};
FuturesInitialRedirect.displayName = 'FuturesInitialRedirect';

export default FuturesInitialRedirect;
